import React, { useState, useEffect } from "react";
import {
  Popconfirm,
  Input,
  Select,
  DatePicker,
  Icon,
  Row,
  Col,
  Form,
  message,
} from "antd";
import moment from "moment";
import "./index.less";
import { formatValue } from "../../../../../../../utils";
import { formatVehicleName } from "../../../../../../../utils/vehicleDisplayName";
import { platformApi } from "../../../../../../../api";

const { Item } = Form;

function ChallanTable({
  toDelete,
  deleteAccess,
  dataSource,
  delete: deleteData,
  loading,
  editData,
  clearRef,
  addVehicle,
  error,
  setError,
  open,
  form,
  manufacturer,
  setLocationDisabled,
}) {
  const [chassisNoValidation, setChassisNoValidation] = useState({});
  const [engineNoValidation, setEngineNoValidation] = useState({});
  const [data, setData] = useState([]);
  const [colors, setColor] = useState([]); // color options per row
  const [vehicles, setVehicles] = useState([]); // current manufacturer’s models
  const [manId, setManId] = useState(null);
  const [modelsCache, setModelsCache] = useState({}); // cache manufacturerId -> models

  const {
    getFieldDecorator,
    setFieldsValue,
    resetFields,
  } = form;

  const chassisNoPattern = /^[A-Z0-9]{17}$/;

  // clearRef setup
  useEffect(() => {
    clearRef.current = () => {
      setColor([]);
      setVehicles([]);
      setData([]);
      resetFields();
    };
    return () => {
      clearRef.current = null;
    };
  }, []);

  // keep data synced
  useEffect(() => {
    setData(dataSource);
  }, [dataSource]);

  // Disable location if multiple vehicles
  useEffect(() => {
    setLocationDisabled(data.length > 1);
  }, [data]);

  // Prefill manufacturer’s vehicle models once per manufacturer id
  useEffect(() => {
    if (dataSource && dataSource.length > 0) {
      const firstRow = dataSource[0];
      const mfgId = firstRow?.vehicle?.manufacturer?.id || firstRow?.vehicle?.Manufacturer?.id || firstRow?.manufacturer?.id || firstRow?.manufacturer;
      const mfgName = firstRow?.vehicle?.manufacturer?.name || firstRow?.vehicle?.Manufacturer?.name || firstRow?.manufacturer?.name || "";

      if (mfgId) {
        setFieldsValue({
          manufacturer: mfgId,
        });

        // Use cache if available
        if (modelsCache[mfgId]) {
          setVehicles(modelsCache[mfgId]);
          populateColors(modelsCache[mfgId], dataSource);
        } else {
          fetchVehicles(mfgId);
        }
      }
    }
  }, [dataSource]);

  // Fetch vehicle models for manufacturer
  const fetchVehicles = async (manufacturerId) => {
    try {
      const result = await platformApi.get(`/api/vehicleMaster/man/${manufacturerId}`);
      const { data } = result;
      if (data.code === 200 && data.response.code === 200) {
        const models = data.response.data.filter(
          (m) => m.vehicleStatus === "AVAILABLE"
        );
        setVehicles(models);
        setModelsCache((prev) => ({ ...prev, [manufacturerId]: models }));
        populateColors(models, dataSource);
      }
    } catch (err) {
      console.error("Error fetching vehicles:", err);
    }
  };

  // Populate color options per row based on vehicle.id match
  const populateColors = (models, rows) => {
    const ColorMap = [];
    rows.forEach((row, index) => {
      const matched = models.find((v) => v.id === row.vehicle?.id);
      if (matched) {
        ColorMap[index] = matched.image || [];
      } else {
        if (row.color) {
          ColorMap[index] = [row.color];
        } else {
          ColorMap[index] = [];
        }
      }
    });
    setColor(ColorMap);
  };

  const handleManufacturerChange = async (id) => {
    if (id === manId) return; // skip same selection
    setManId(id);
    setVehicles([]);
    setColor([]);
    if (modelsCache[id]) {
      setVehicles(modelsCache[id]);
    } else {
      await fetchVehicles(id);
    }
    // clear models/colors when manufacturer changes
    const cleared = data.map((d) => ({
      ...d,
      vehicle: {},
      color: {},
      chassisNo: "",
      engineNo: "",
      keyNo: "",
      batteryNo: "",
      warrantyBookNo: "",
      manMonthYear: "",
    }));
    setData(cleared);
    editData(cleared);
  };

  const handleChange = (event, index, type) => {
    let updated = [...data];
    let value = event?.target ? formatValue(event, "code") : event;
    if ((type === "chassisNo" || type === "engineNo") && typeof value === "string") {
      value = value.toUpperCase();
    }
    updated[index][type] = value;
    setData(updated);
    editData(updated);
  };

  const handleModelChange = (id, index) => {
    const selected = vehicles.find((v) => v.id === id);
    if (!selected) return;

    const updated = [...data];
    updated[index].vehicle = selected;
    updated[index].color = {}; // clear old color

    const newColors = [...colors];
    newColors[index] = selected.image || [];
    setColor(newColors);

    setData(updated);
    editData(updated);
  };

  const handleColorChange = (colorId, index) => {
    const selectedColor = colors[index]?.find((c) => c.id === colorId);
    if (!selectedColor) return;
    handleChange(selectedColor, index, "color");
  };

  const disableMonth = (current) => moment().isBefore(current, "month");

  const validateChassisNo = (record, chassisNo, index) => {
    if (!record.vehicle?.id) return;
    const rowKey = record.vehicle.id + "_" + index;
    const upper = chassisNo.toUpperCase();

    if (!chassisNoPattern.test(upper)) {
      return setChassisNoValidation((prev) => ({
        ...prev,
        [rowKey]: "Enter Valid Chassis Number",
      }));
    }

    platformApi
      .post(`/api/purchaseInvoice/checkChassisNo`, {
        chassisNo: upper,
        manufacturer: record.vehicle.manufacturer?.id,
      })
      .then(({ data }) => {
        if (data.code === 200 && data.response.code === 200) {
          if (data.response.data === true) {
            setChassisNoValidation((prev) => ({
              ...prev,
              [rowKey]: "Chassis Number Already Exists",
            }));
          } else {
            setChassisNoValidation((prev) => ({ ...prev, [rowKey]: false }));
            const frameDate = data.response.frameDate;
            if (frameDate) {
              const formattedDate = moment(frameDate).format("MMM-YYYY");
              const updated = [...dataSource];
              updated[index].manMonthYear = formattedDate;
              setData(updated);
              editData(updated);
              if (index === dataSource.length - 1) addVehicle();
            }
          }
        }
      })
      .catch(() => {
        setChassisNoValidation((prev) => ({
          ...prev,
          [rowKey]: "Error validating chassis number",
        }));
      });
  };

  const renderVehicleCard = (record, index) => {
    const chassisKey = record.vehicle?.id
      ? `${record.vehicle.id}_${index}`
      : `row_${index}`;

    return (
      <div
        key={record.id || index}
        className="vpi-vehicle-card"
      >
        {toDelete && deleteAccess && (
          <div className="vpi-vehicle-card__delete">
            <Popconfirm
              title="Are you sure delete this Vehicle?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => {
                if (dataSource.length === 1) {
                  message.error("At least one vehicle required", 2);
                } else deleteData(record.id, index);
              }}
            >
              <Icon type="delete" />
            </Popconfirm>
          </div>
        )}

        {/* Row 1: Model (wide) + Color + Chassis No + Engine No */}
        <Row gutter={[8, 8]} type="flex" align="top">
          <Col span={9}>
            <div className="vpi-vehicle-card__label">Model</div>
            <Select
              className="vehicle-select vpi-vehicle-card__model"
              showSearch
              value={record.vehicle?.id || undefined}
              onChange={(val) => handleModelChange(val, index)}
              disabled={!toDelete}
              placeholder="Model"
              optionFilterProp="children"
              style={{ width: "100%" }}
            >
              {record.vehicle?.id &&
                !vehicles.some((v) => v.id === record.vehicle.id) && (
                  <Select.Option
                    key={record.vehicle.id}
                    value={record.vehicle.id}
                  >
                    {formatVehicleName(record.vehicle)}
                  </Select.Option>
                )}
              {vehicles.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {formatVehicleName(v)}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={5}>
            <div className="vpi-vehicle-card__label">Color</div>
            <Select
              value={record.color?.id || undefined}
              placeholder="Color"
              onChange={(val) => handleColorChange(val, index)}
              disabled={!toDelete}
              style={{ width: "100%" }}
            >
              {record.color?.id &&
                !colors[index]?.some((c) => c.id === record.color.id) && (
                  <Select.Option
                    key={record.color.id}
                    value={record.color.id}
                  >
                    {record.color.color}
                  </Select.Option>
                )}
              {colors[index]?.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.color}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={5}>
            <div className="vpi-vehicle-card__label">Chassis No</div>
            <Form.Item
              validateStatus={chassisNoValidation[chassisKey] ? "error" : "success"}
              help={chassisNoValidation[chassisKey]}
              style={{ marginBottom: 0 }}
            >
              <Input
                value={record.chassisNo}
                maxLength={17}
                onChange={(e) => {
                  handleChange(e, index, "chassisNo");
                  validateChassisNo(record, e.target.value, index);
                }}
                disabled={!toDelete}
                placeholder="Chassis No"
                style={{ textTransform: "uppercase" }}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <div className="vpi-vehicle-card__label">Engine No</div>
            <Input
              value={record.engineNo}
              onChange={(e) => handleChange(e, index, "engineNo")}
              maxLength={17}
              disabled={!toDelete}
              placeholder="Engine No"
              style={{ textTransform: "uppercase" }}
            />
          </Col>
        </Row>

        {/* Row 2: Key No + Battery No + WRC + MFG Month */}
        <Row gutter={[8, 8]} type="flex" align="top" style={{ marginTop: 8 }}>
          <Col span={6}>
            <div className="vpi-vehicle-card__label">Key No</div>
            <Input
              value={record.keyNo}
              onChange={(e) => handleChange(e, index, "keyNo")}
              maxLength={17}
              disabled={!toDelete}
              placeholder="Key No"
              style={{ textTransform: "uppercase" }}
            />
          </Col>
          <Col span={6}>
            <div className="vpi-vehicle-card__label">Battery No</div>
            <Input
              value={record.batteryNo}
              onChange={(e) => handleChange(e, index, "batteryNo")}
              maxLength={17}
              disabled={!toDelete}
              placeholder="Battery No"
              style={{ textTransform: "uppercase" }}
            />
          </Col>
          <Col span={6}>
            <div className="vpi-vehicle-card__label">WRC</div>
            <Input
              value={record.warrantyBookNo}
              onChange={(e) => handleChange(e, index, "warrantyBookNo")}
              maxLength={17}
              disabled={!toDelete}
              placeholder="Warranty Book No"
              style={{ textTransform: "uppercase" }}
            />
          </Col>
          <Col span={6}>
            <div className="vpi-vehicle-card__label">MFG Month</div>
            <DatePicker.MonthPicker
              value={
                record.manMonthYear
                  ? moment(record.manMonthYear, "MMM-YYYY")
                  : null
              }
              disabled
              format="MMM-YYYY"
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <Form>
      <span>Manufacturer</span>
      <Row>
        <Col span={6}>
          <Item>
            {getFieldDecorator("manufacturer")(
              <Select
                showSearch
                disabled={!toDelete}
                placeholder="Manufacturer"
                onChange={handleManufacturerChange}
                style={{ width: "100%" }}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 320 }}
                filterOption={(input, option) =>
                  option.props.children
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {manufacturer.map((m) => (
                  <Select.Option key={m.id} value={m.id} title={m.name}>
                    {m.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Item>
        </Col>
      </Row>

      <span>Vehicle Details</span>
      <div
        className={`vpi-vehicle-cards${
          loading && (loading.spinning === true || loading === true)
            ? " vpi-vehicle-cards--loading"
            : ""
        }`}
      >
        {(data || []).map((record, index) => renderVehicleCard(record, index))}
        {(!data || data.length === 0) && (
          <div className="vpi-vehicle-cards__empty">No vehicles</div>
        )}
      </div>
    </Form>
  );
}

const WrappedChallanTable = Form.create()(ChallanTable);
export default WrappedChallanTable;


// import React, { useState } from "react";
// import {
//   Table,
//   Popconfirm,
//   Input,
//   Select,
//   DatePicker,
//   Divider,
//   Icon,
//   Row,
//   Col,
//   Form,
//   message,
// } from "antd";
// import { useEffect } from "react";
// import moment from "moment";
// import "./index.less";
// import { formatValue } from "../../../../../../../utils";
// import { platformApi } from "../../../../../../../api";

// const { Item } = Form;
// function ChallanTable({
//   toDelete,
//   dataSource,
//   delete: deleteData,
//   loading,
//   editData,
//   clearRef,
//   addVehicle,
//   error,
//   setError,
//   open,
//   form,
//   manufacturer,
//   setLocationDisabled,
// }) {
//   const [chassisNoValidation, setChassisNoValidation] = useState({});
//   const [engineNoValidation, setEngineNoValidation] = useState({});

//   const [data, setData] = useState([]);
//   const [colors, setColor] = useState([]);
//   // const [manufacturer, setManufacturer] = useState([]);
//   const [vehicles, setVehicle] = useState([]);
//   const [vehiclecolor, setVehiclecolor] = useState([]);
//   const [manId, setManId] = useState(null);
//   const {
//     getFieldDecorator,
//     setFieldsValue,
//     getFieldValue,
//     validateFields,
//     resetFields,
//   } = form;
//   const chassisNoPattern = /^[A-Z0-9]{17}$/;
//   const engineNoPattern = /^[a-zA-Z0-9]{5}$/;

//   useEffect(() => {
//     clearRef.current = () => {
//       setColor([]);
//       // setManufacturer([]);
//       setVehiclecolor([]);
//       setVehicle([]);
//       setData([]);
//       resetFields();
//     };
//     return () => {
//       clearRef.current = null;
//     };
//   });

//   useEffect(() => {
//     if (dataSource && dataSource.length > 0) {
//       let Color = [...colors];
//       dataSource.forEach((datum, index) => {
//         if (datum.color) {
//           // At minimum, include the current color as an option
//           Color[index] = [datum.color];
//         }
//       });
//       setColor(Color);
//     }
//   }, [dataSource]);

//   const handleChange = (event, index, type) => {
//     let data = dataSource;
//     if (event.target) {
//       let value = formatValue(event, "code");
//       // Ensure chassisNo is always uppercase
//       if (type === "chassisNo") {
//         value = value.toUpperCase();
//       }
//       data[index][type] = value;
//     } else {
//       // Ensure chassisNo is always uppercase
//       if (type === "chassisNo" && typeof event === "string") {
//         data[index][type] = event.toUpperCase();
//       } else {
//         data[index][type] = event;
//       }
//     }

//     setData([...data]);
//     editData([...data]);
//   };

//   const validateChassisNo = (record, chassisNo, index) => {
//     if (record.vehicle?.id == null) return;
//     const rowKey = record.vehicle?.id
//       ? `${record.vehicle.id}_${index}`
//       : `row_${index}`;

//     const chassisNoUpper = chassisNo.toUpperCase();

//     const matchingIndices = dataSource
//       .map((item, idx) =>
//         item.chassisNo && item.chassisNo.toUpperCase() === chassisNoUpper
//           ? idx
//           : -1
//       )
//       .filter((idx) => idx !== -1);

//     if (matchingIndices.length > 1 && index !== matchingIndices[0]) {
//       setChassisNoValidation((prev) => ({
//         ...prev,
//         [rowKey]: "Chassis Number already taken",
//       }));
//       return;
//     }

//     if (!chassisNoPattern.test(chassisNoUpper)) {
//       return setChassisNoValidation((prev) => ({
//         ...prev,
//         [rowKey]: "Enter Valid Chassis Number",
//       }));
//     }

//     platformApi
//       .post(`/api/purchaseInvoice/checkChassisNo`, {
//         chassisNo: chassisNoUpper,
//         manufacturer: record?.vehicle?.manufacturer?.id,
//       })
//       .then((result) => {
//         const { data } = result;
//         if (data.code === 200) {
//           const { response } = data;

//           if (response.data === true) {
//             setChassisNoValidation((prev) => ({
//               ...prev,
//               [rowKey]: "Chassis Number Already Exists",
//             }));
//           } else {
//             setChassisNoValidation((prev) => ({ ...prev, [rowKey]: false }));

//             const frameDate = response.frameDate;
//             if (frameDate) {
//               const formattedDate = new Date(frameDate).toLocaleDateString(
//                 "en-us",
//                 {
//                   month: "short",
//                   year: "numeric",
//                 }
//               );

//               const updated = [...dataSource];
//               updated[index]["manMonthYear"] = formattedDate;

//               editData(updated);
//               setData(updated);

//               if (index === dataSource.length - 1) {
//                 addVehicle();
//               }
//             }
//           }
//         }
//       })
//       .catch((err) => {
//         setChassisNoValidation((prev) => ({
//           ...prev,
//           [rowKey]: "Error validating chassis number",
//         }));
//       });
//   };

//   const validateEngineNoDuplicate = (record, engineNo, index) => {
//     if (record.vehicle?.id == null) return;
//     const rowKey = record.vehicle?.id
//       ? `${record.vehicle.id}_${index}`
//       : `row_${index}`;

//     const engineNoUpper = engineNo.toUpperCase();

//     if (engineNoUpper.length < 11) {
//       setEngineNoValidation((prev) => ({
//         ...prev,
//         [rowKey]: "Enter Valid Engine Number",
//       }));
//       return;
//     }

//     const matchingIndices = dataSource
//       .map((item, idx) =>
//         item.engineNo && item.engineNo.toUpperCase() === engineNoUpper
//           ? idx
//           : -1
//       )
//       .filter((idx) => idx !== -1);

//     if (matchingIndices.length > 1 && index !== matchingIndices[0]) {
//       setEngineNoValidation((prev) => ({
//         ...prev,
//         [rowKey]: "Engine Number already taken",
//       }));
//       return;
//     }

//     // Backend validation for engine number
//     platformApi
//       .post(`/api/purchaseInvoice/checkEngineNo`, {
//         engineNo: engineNoUpper,
//         manufacturer: record?.vehicle?.manufacturer?.id,
//       })
//       .then((result) => {
//         const { data } = result;
//         if (data.code === 200) {
//           const { response } = data;
//           if (response.data === true) {
//             setEngineNoValidation((prev) => ({
//               ...prev,
//               [rowKey]: "Engine Number Already Exists",
//             }));
//           } else {
//             setEngineNoValidation((prev) => ({ ...prev, [rowKey]: false }));
//           }
//         }
//       })
//       .catch((err) => {
//         setEngineNoValidation((prev) => ({
//           ...prev,
//           [rowKey]: "Error validating engine number",
//         }));
//       });
//   };

//   useEffect(() => {
//     if (data.length > 1) {
//       setLocationDisabled(true);
//     } else {
//       setLocationDisabled(false);
//     }
//   }, [data]);

//   useEffect(() => {
//     setData(dataSource);
  
//     if (
//       dataSource &&
//       dataSource[0] &&
//       dataSource[0].vehicle &&
//       dataSource[0].vehicle.manufacturer &&
//       dataSource[0].vehicle.manufacturer.id
//     ) {
//       setFieldsValue({ manufacturer: dataSource[0].vehicle.manufacturer.name });
      
//       // Fetch vehicles first
//       const manufacturerId = dataSource[0].vehicle.manufacturer.id;
//       platformApi.get(`/api/vehicleMaster/man/${manufacturerId}`).then((result) => {
//         let { data } = result;
//         if (data.code === 200) {
//           let { response } = data;
//           if (response.code === 200) {
//             let models = response.data.filter(
//               (model) => model.vehicleStatus === "AVAILABLE"
//             );
//             setVehicle(models);
            
//             // NOW set colors by matching prefilled vehicles with fetched data
//             let Color = [];
//             dataSource.forEach((datum, index) => {
//               const matchedVehicle = models.find(v => v.id === datum.vehicle.id);
//               if (matchedVehicle) {
//                 Color[index] = matchedVehicle.image; // Get full color array from API
//               }
//             });
//             setColor(Color);
//           }
//         }
//       });
//     }
//   }, [dataSource]);

//   const handleManufacturerChange = (id) => {
//     // let vehicle = vehicles;
//     setManId(id);
//     let models = [];
//     platformApi.get(`/api/vehicleMaster/man/${id}`).then((result) => {
//       let { data } = result;
//       if (data.code === 200) {
//         let { response } = data;
//         if (response.code === 200) {
//           setVehicle(response.data);
//           response.data.map((model) => {
//             if (model.vehicleStatus === "AVAILABLE") {
//               models.push(model);
//             }
//           });
//           setVehicle(models);
//         }
//       }
//     });
//   };
//   const handleMonthChange = (id, index) => {
//     const date = new Date(id).toLocaleDateString("en-us", {
//       month: "short",
//       year: "numeric",
//     });
//     handleChange(date, index, "manMonthYear");
//   };
//   const handleModelChange = (id, index) => {
//     let Color = colors;
//     vehicles.some((vehicle) => {
//       if (vehicle.id === id) {
//         handleChange(vehicle, index, "vehicle");
//         Color[index] = vehicle.image;
//         setColor([...Color]);
//       }
//     });
//   };

  
//   const handleColorChange = (id, index) => {
//     vehiclecolor.some((color) => {
//       if (color.id === id) {
//         handleChange(color, index, "color");
//       }
//     });
//   };
//   const disableMonth = (current) => {
//     const date = moment(Date.now());
//     if (date <= current) {
//       return true;
//     } else return false;
//   };

//   const column = [
//     {
//       title: "Model",
//       dataIndex: "vehicle",
//       key: "vehicle",
//       width: 200,
//       render: (data, record, index) => {
//         const modelValue =
//           record.vehicle && record.vehicle.id ? record.vehicle.id : undefined;
//         return (
//           <Select
//             className="ChallanTable-Manufacturers"
//             dropdownStyle={{
//               overflowY: "auto",
//               overflowX: "hidden",
//             }}
//             onChange={(event) => {
//               handleModelChange(event, index);
//             }}
//             showSearch
//             value={modelValue}
//             disabled={!toDelete || !(vehicles.length >= 1)}
//             placeholder="Model"
//             filterOption={(input, option) =>
//               option.props.children.some(
//                 (child) => child.toLowerCase().indexOf(input.toLowerCase()) >= 0
//               )
//             }
//           >
//             {vehicles.map((vehicle) => (
//               <Select.Option key={vehicle.id} value={vehicle.id}>
//                 {vehicle.modelCode} - {vehicle.modelName}
//               </Select.Option>
//             ))}
//           </Select>
//         );
//       },
//     },
//     {
//       title: "Color",
//       dataIndex: "color",
//       key: "color",
//       width: 100,
//       render: (data, record, index) => {
//         const colorValue = record.color ? record.color : undefined;
        
//         return (
//           <Select
//             className="ChallanTable-Manufacturers"
//             dropdownStyle={{
//               overflowY: "auto",
//               overflowX: "hidden",
//             }}
//             onFocus={() => {
//               setVehiclecolor(colors[index]);
//             }}
//             onChange={(event) => {
//               handleColorChange(event, index);
//             }}
//             disabled={!toDelete}
//             placeholder="Color"
//             value={colorValue.color}
//           >
//             {vehiclecolor?.map((color) => (
//               <Select.Option key={color.id} value={color.id}>
//                 {color.color}
//               </Select.Option>
//             ))}
//           </Select>
//         );
//       },
//     },
//     {
//       title: " Chassis No",
//       dataIndex: "chassisNo",
//       key: "chassisNo",
//       width: 160,
//       render: (data, record, index) => (
//         <Form.Item
//           validateStatus={`${
//             chassisNoValidation[
//               record.vehicle?.id
//                 ? `${record.vehicle.id}_${index}`
//                 : `row_${index}`
//             ]
//               ? "error"
//               : "success"
//           }`}
//           help={
//             chassisNoValidation[
//               record.vehicle?.id
//                 ? `${record.vehicle.id}_${index}`
//                 : `row_${index}`
//             ]
//           }
//         >
//           <Input
//             style={{ textTransform: "uppercase" }}
//             key="chassisNo"
//             pattern="^[A-Z0-9]{17}$"
//             onInput={(event) => {
//               handleChange(event, index, "chassisNo");
//               validateChassisNo(record, event.target.value, index);
//             }}
//             onWheel={(event) => event.currentTarget.blur()}
//             maxLength={17}
//             // disabled={!toDelete}
//             disabled={!toDelete || !(vehicles.length >= 1)}
//             placeholder="Chassis No"
//             value={data}
//           />
//         </Form.Item>
//       ),
//     },
//     {
//       title: "Engine No",
//       dataIndex: "engineNo",
//       key: "engineNo",
//       width: 160,
//       render: (data, record, index) => (
//         <Form.Item
//           validateStatus={
//             engineNoValidation[
//               record.vehicle?.id
//                 ? `${record.vehicle.id}_${index}`
//                 : `row_${index}`
//             ]
//               ? "error"
//               : "success"
//           }
//           help={
//             engineNoValidation[
//               record.vehicle?.id
//                 ? `${record.vehicle.id}_${index}`
//                 : `row_${index}`
//             ]
//           }
//         >
//           <Input
//             style={{ textTransform: "uppercase" }}
//             key="engineNo"
//             onChange={(event) => {
//               handleChange(event, index, "engineNo");
//               validateEngineNoDuplicate(record, event.target.value, index);
//             }}
//             maxLength={17}
//             onWheel={(event) => event.currentTarget.blur()}
//             disabled={!toDelete}
//             placeholder="Engine No"
//             value={data}
//           />
//         </Form.Item>
//       ),
//     },
//     {
//       title: "Key no",
//       dataIndex: "keyNo",
//       key: "keyNo",
//       width: 160,
//       render: (data, record, index) => {
//         return (
//           <Input
//             style={{ textTransform: "uppercase" }}
//             key="keyNo"
//             onChange={(event) => {
//               handleChange(event, index, "keyNo");
//             }}
//             onWheel={(event) => event.currentTarget.blur()}
//             disabled={!toDelete}
//             placeholder="Key No"
//             value={data}
//             maxLength={17}
//           />
//         );
//       },
//     },
//     {
//       title: "Battery No",
//       dataIndex: "batteryNo",
//       key: "batteryNo",
//       width: 160,
//       render: (data, record, index) => (
//         <Input
//           style={{ textTransform: "uppercase" }}
//           key="batteryNo"
//           onChange={(event) => {
//             handleChange(event, index, "batteryNo");
//           }}
//           onWheel={(event) => event.currentTarget.blur()}
//           disabled={!toDelete}
//           value={data}
//           placeholder="Battery No"
//           maxLength={17}
//         />
//       ),
//     },
//     {
//       title: "WRC",
//       dataIndex: "warrantyBookNo",
//       key: "warrantyBookNo",
//       width: 160,
//       render: (data, record, index) => (
//         <Input
//           style={{ textTransform: "uppercase" }}
//           key="warrantyBookNo"
//           onChange={(event) => {
//             handleChange(event, index, "warrantyBookNo");
//           }}
//           onWheel={(event) => event.currentTarget.blur()}
//           disabled={!toDelete}
//           placeholder="Warranty Book No"
//           value={data}
//           maxLength={17}
//         />
//       ),
//     },
//     {
//       title: "MFG Month",
//       dataIndex: "manMonthYear",
//       key: "manMonthYear",
//       width: 100,
//       render: (data, record, index) => (
//         <DatePicker.MonthPicker
//           onChange={(event) => {
//             handleMonthChange(event, index);
//           }}
//           disabledDate={disableMonth}
//           style={{ width: "100%" }}
//           format="MMM-YYYY"
//           disabled={true}
//           placeholder="Month"
//           value={data ? moment(data, "MMM-YYYY") : null}
//         />
//       ),
//     },
//     {
//       key: "actions",
//       width: 30,
//       render: (data1, data2, index) =>
//         toDelete && (
//           <div>
//             <Popconfirm
//               title="Are you sure delete this Vehicle?"
//               okText="Yes"
//               cancelText="No"
//               onCancel={(event) => {}}
//               onConfirm={(event) => {
//                 if (
//                   dataSource.length === 1 &&
//                   dataSource.length - 1 === index
//                 ) {
//                   message.error(
//                     "Vehicle cannot be deleted as there is only one vehicle",
//                     2
//                   );
//                 } else {
//                   deleteData(data1.id, index);
//                 }
//               }}
//             >
//               <Icon type="delete" />
//             </Popconfirm>
//           </div>
//         ),
//     },
//   ];

//   return (
//     <Form>
//       <span>Manufacturer</span>
//       <Row>
//         <Col span={4}>
//           <Item>
//             {getFieldDecorator("manufacturer")(
//               <Select
//                 showSearch={true}
//                 disabled={!toDelete}
//                 filterOption={(input, option) =>
//                   option.props.children
//                     .toLowerCase()
//                     .indexOf(input.toLowerCase()) >= 0
//                 }
//                 onChange={handleManufacturerChange}
//                 initialValue={
//                   data
//                     ? data[0]
//                       ? data[0].vehicle
//                         ? data[0].vehicle.manufacturer
//                           ? data[0].vehicle.manufacturer.name
//                           : undefined
//                         : undefined
//                       : undefined
//                     : undefined
//                 }
//                 placeholder="Manufacturer"
//               >
//                 {manufacturer.map((manufacturer) => (
//                   <Select.Option key={manufacturer.id} value={manufacturer.id}>
//                     {manufacturer.name}
//                   </Select.Option>
//                 ))}
//               </Select>
//             )}
//           </Item>
//         </Col>
//       </Row>
//       <span>Vehicle Details</span>
//       <Table
//         size="small"
//         loading={loading}
//         pagination={false}
//         scroll={{ y: 160 }}
//         className="Challantable"
//         columns={column.filter((obj) => obj.title !== "Action" || toDelete)}
//         style={{ cursor: "pointer" }}
//         dataSource={data}
//       />
//     </Form>
//   );
// }
// const WrappedChallanTable = Form.create()(ChallanTable);

// export default WrappedChallanTable;
